import { CountryCode } from "constants/enums";

export const OMS_REGIONS = {
  INDIA: "india",
  US: "us",
  GCC: "gcc",
};

export const INDIA_COUNTRY_ID = 1;
export const USA_COUNTRY_ID = 8;
export const GCC_COUNTRY_IDS = [2, 3, 4, 5, 6, 7];

/** Stable ISD → region map for phone-number search. */
export const OMS_REGION_BY_ISD_CODE = {
  "+91": OMS_REGIONS.INDIA,
  "+1": OMS_REGIONS.US,
  "+971": OMS_REGIONS.GCC,
  "+966": OMS_REGIONS.GCC,
  "+974": OMS_REGIONS.GCC,
  "+968": OMS_REGIONS.GCC,
  "+965": OMS_REGIONS.GCC,
  "+973": OMS_REGIONS.GCC,
};

export const ISD_FLAG_BY_CODE = {
  "+91": "🇮🇳",
  "+1": "🇺🇸",
};

export const formatIsdDropdownLabel = (isdCode) => {
  const normalized = String(isdCode ?? "").trim();
  const flag = ISD_FLAG_BY_CODE[normalized];
  return flag ? `${flag} ${normalized}` : normalized;
};

/** Country code → region map. */
export const OMS_REGION_BY_COUNTRY_CODE = {
  [CountryCode.IND]: OMS_REGIONS.INDIA,
  [CountryCode.USA]: OMS_REGIONS.US,
  [CountryCode.BHR]: OMS_REGIONS.GCC,
  [CountryCode.KWT]: OMS_REGIONS.GCC,
  [CountryCode.QAT]: OMS_REGIONS.GCC,
  [CountryCode.OMN]: OMS_REGIONS.GCC,
  [CountryCode.SAU]: OMS_REGIONS.GCC,
  [CountryCode.ARE]: OMS_REGIONS.GCC,
};

const OMS_REGION_BY_COUNTRY_ID = {
  [INDIA_COUNTRY_ID]: OMS_REGIONS.INDIA,
  [USA_COUNTRY_ID]: OMS_REGIONS.US,
  ...Object.fromEntries(
    GCC_COUNTRY_IDS.map((countryId) => [countryId, OMS_REGIONS.GCC]),
  ),
};

const trimBaseUrl = (url) => {
  if (url == null) {
    return "";
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\/+$/, "");
};

export const OMS_BASE_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_URL),
  [OMS_REGIONS.US]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_US_URL),
  [OMS_REGIONS.GCC]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_GCC_URL),
};

/** Full unified search-user URLs per region (includes path). */
export const SEARCH_USER_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_URL,
  ),
  [OMS_REGIONS.US]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_US_URL,
  ),
  [OMS_REGIONS.GCC]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_GCC_URL,
  ),
};

export const resolveOmsRegion = ({
  countryId,
  countryCode,
  isdCode,
} = {}) => {
  if (isdCode) {
    const regionFromIsd = OMS_REGION_BY_ISD_CODE[String(isdCode).trim()];
    if (regionFromIsd) {
      return regionFromIsd;
    }
  }

  if (countryCode) {
    const regionFromCode =
      OMS_REGION_BY_COUNTRY_CODE[String(countryCode).trim().toUpperCase()];
    if (regionFromCode) {
      return regionFromCode;
    }
  }

  if (countryId != null && countryId !== "") {
    const regionFromId = OMS_REGION_BY_COUNTRY_ID[Number(countryId)];
    if (regionFromId) {
      return regionFromId;
    }
  }

  return OMS_REGIONS.INDIA;
};

export const isLearnOnlyRegion = (options = {}) => {
  const region = resolveOmsRegion(options);
  return region === OMS_REGIONS.US || region === OMS_REGIONS.GCC;
};

/**
 * Live country context from the current URL.
 * Prefer this over session/local storage so a refresh always re-reads countryId.
 */
export const getCountryIdFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search).get("countryId");
};

export const getIsdCodeFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search).get("isdCode");
};

const resolveRegionFromOptions = (options = {}) => {
  const countryId =
    options.countryId != null && options.countryId !== ""
      ? options.countryId
      : getCountryIdFromUrl();
  const isdCode =
    options.isdCode != null && options.isdCode !== ""
      ? options.isdCode
      : getIsdCodeFromUrl();

  return resolveOmsRegion({
    countryId,
    countryCode: options.countryCode,
    isdCode,
  });
};

const getRegionalUrl = ({
  options = {},
  urlByRegion,
  envKeyByRegion,
  serviceLabel,
}) => {
  const region = resolveRegionFromOptions(options);
  const baseUrl = urlByRegion[region];

  if (!baseUrl) {
    throw new Error(
      `${serviceLabel} URL is not configured for region "${region}". Set ${envKeyByRegion[region]}.`,
    );
  }

  return baseUrl;
};

/**
 * Resolves ONE OMS base URL by customer country / ISD.
 * Prefer env URLs per region; India keeps existing REACT_APP_ONE_OMS_URL.
 */
export const getOneOmsBaseUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: OMS_BASE_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_ONE_OMS_URL",
      [OMS_REGIONS.US]: "REACT_APP_ONE_OMS_US_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_ONE_OMS_GCC_URL",
    },
    serviceLabel: "ONE OMS",
  });

/**
 * Resolves unified search-user URL by customer country / ISD.
 * US staging: https://usdev-gatewayapi.devinfinitylearn.in/api/v1/unified/search-user
 */
export const getSearchUserUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: SEARCH_USER_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_ONE_APP_SEARCH_USER_URL",
      [OMS_REGIONS.US]: "REACT_APP_ONE_APP_SEARCH_USER_US_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_ONE_APP_SEARCH_USER_GCC_URL",
    },
    serviceLabel: "Search user",
  });

/** Build customer-orders path with region query params. */
export const buildCustomerOrdersPath = ({
  customerId,
  countryId,
  isdCode,
}) => {
  const params = new URLSearchParams({ customerId: String(customerId) });
  if (countryId != null && countryId !== "") {
    params.set("countryId", String(countryId));
  }
  if (isdCode) {
    params.set("isdCode", String(isdCode));
  }
  return `/customerOrders?${params.toString()}`;
};

/** Preserve region params when navigating to order details. */
export const buildOrderDetailsPath = (orderId, searchParams) => {
  const params = new URLSearchParams({ order_id: String(orderId) });
  const countryId =
    searchParams?.get?.("countryId") ?? getCountryIdFromUrl();
  const isdCode = searchParams?.get?.("isdCode") ?? getIsdCodeFromUrl();
  if (countryId) {
    params.set("countryId", countryId);
  }
  if (isdCode) {
    params.set("isdCode", isdCode);
  }
  return `/orderDetails?${params.toString()}`;
};
